/**
 * Azure AI Speech engines for the candidate interview (Track 2, docs/VOICE_STACK_DECISION.md).
 *
 * WHY THIS FILE EXISTS. The interview's original ears and voice were the browser's free Web
 * Speech API, and real candidates paid for that choice: Chrome ends recognition sessions at
 * will (words spoken in the restart gap are lost), `speechSynthesis` is OS audio the mic's
 * echo-canceller cannot see (so the mic had to stay shut around Asha's speech, clipping prompt
 * answerers), and the free recogniser under-hears accented English. These engines replace both
 * directions with Azure AI Speech in Central India:
 *
 *  - RECOGNITION streams to a real ASR with language identification over en-IN / en-US / en-GB —
 *    "universal English, primarily Indian candidates": anyone is understood, nobody configures
 *    anything.
 *  - SYNTHESIS renders a neural voice through a page `<audio>` pipeline (SpeakerAudioDestination),
 *    which makes Asha's voice visible to the browser's echo cancellation — the reason the old
 *    450ms tail guard existed disappears at the source.
 *
 * DESIGN RULES.
 *  1. The SDK (~1 MB) is loaded lazily, only when the interview actually gets an Azure session.
 *  2. The hooks mirror the Web Speech hooks' contracts EXACTLY (VoiceInput / asha-speech shape),
 *     so InterviewStep selects an engine and drives it identically. The unselected engine is
 *     never driven and stays idle.
 *  3. The browser never sees the subscription key — only the ~10-minute STS token minted by
 *     GET /invite/{token}/interview/speech, refreshed on a timer while the interview runs.
 *  4. Everything degrades: if the session fetch fails, the SDK fails to load, or recognition
 *     refuses to stay up, the caller falls back to the Web Speech engines. Never trapped.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { ttsBillableCharacters } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import type { SpeechUsageSink } from "./speechUsage.js";

/** The server's answer to "may this interview use Azure Speech, and how?". */
export interface SpeechSession {
  enabled: boolean;
  token?: string;
  region?: string;
  ttsVoice?: string;
  sttLocales?: string[];
  refreshSeconds?: number;
}

/** Mirror of the Web Speech VoiceInput contract (InterviewStep). */
export interface AzureVoiceInput {
  supported: boolean;
  listening: boolean;
  blocked: boolean;
  finalText: string;
  interim: string;
  transcript: string;
  confidence: number | null;
  setIntent: (want: boolean) => void;
  reset: () => void;
}

type Sdk = typeof import("microsoft-cognitiveservices-speech-sdk");

let sdkPromise: Promise<Sdk | null> | null = null;
/** One lazy SDK load per page. Null = load failed (offline chunk, CSP…) → caller falls back. */
function loadSdk(): Promise<Sdk | null> {
  sdkPromise ??= import("microsoft-cognitiveservices-speech-sdk").catch(() => null);
  return sdkPromise;
}

/**
 * Fetch the speech session for this interview and keep its token fresh.
 *
 *  - `undefined` → still resolving (don't speak the first question yet — choosing an engine and
 *    then switching voices mid-interview would be worse than a moment's wait);
 *  - `{enabled:false}` → use the Web Speech fallback;
 *  - `{enabled:true, …}` → drive the Azure engines below.
 *
 * The token ref is refreshed by refetching the endpoint (STS tokens cannot be renewed
 * client-side — that would need the key, which the browser must never hold). A refresh failure
 * leaves the old token in place; recognition then fails at the boundary and the recogniser's
 * own retry path re-reads the ref on its next start.
 */
export function useSpeechSession(inviteToken: string): {
  session: SpeechSession | undefined;
  tokenRef: RefObject<string>;
} {
  const [session, setSession] = useState<SpeechSession | undefined>(undefined);
  const tokenRef = useRef<string>("");

  useEffect(() => {
    if (!inviteToken) {
      setSession({ enabled: false });
      return;
    }
    let alive = true;
    let timer: number | null = null;
    const fetchSession = async (first: boolean): Promise<void> => {
      try {
        const s = await api.get<SpeechSession>(`/invite/${inviteToken}/interview/speech`);
        if (!alive) return;
        if (s.token) tokenRef.current = s.token;
        if (first) setSession(s);
        const refreshMs = Math.max(60, s.refreshSeconds ?? 480) * 1000;
        if (s.enabled) timer = window.setTimeout(() => void fetchSession(false), refreshMs);
      } catch {
        if (!alive) return;
        // The endpoint being unreachable is exactly the moment to prefer the engine that needs
        // no server at all.
        if (first) setSession({ enabled: false });
      }
    };
    void fetchSession(true);
    return () => {
      alive = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [inviteToken]);

  return { session, tokenRef };
}

/** First retry delay / ceiling for putting recognition back after an unexpected end. */
const RESTART_BASE_MS = 300;
const RESTART_MAX_MS = 4_000;
/** Consecutive failed sessions after which the mic is declared unusable (mirror of voiceGate). */
const MAX_FAILURES = 6;

/**
 * Azure continuous recognition with the exact VoiceInput contract. The caller owns INTENT
 * (setIntent), this hook reconciles reality to it — same split, same reasons, as the Web Speech
 * hook it replaces: the recogniser restarts under the hook's control, never from a render loop.
 */
export function useAzureVoiceInput(
  active: boolean,
  session: SpeechSession | undefined,
  tokenRef: RefObject<string>,
  acceptResult: (at: number) => boolean,
  /** Live usage measurement (speechUsage.ts). Measures only — never changes recognition. */
  usage: SpeechUsageSink | null = null,
): AzureVoiceInput {
  const [listening, setListening] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [interim, setInterim] = useState("");
  const [confidence, setConfidence] = useState<number | null>(null);
  const confSumRef = useRef(0);
  const confCountRef = useRef(0);

  const acceptRef = useRef(acceptResult);
  acceptRef.current = acceptResult;
  const usageRef = useRef(usage);
  usageRef.current = usage;

  // The live recogniser + bookkeeping. gen guards stale async callbacks (SDK callbacks arrive
  // long after close() when the network is slow).
  const recRef = useRef<{ close: () => void } | null>(null);
  const genRef = useRef(0);
  const runningRef = useRef(false);
  const intentRef = useRef(false);
  const blockedRef = useRef(false);
  const failuresRef = useRef(0);
  const restartRef = useRef<number | null>(null);
  const startRef = useRef<() => void>(() => undefined);

  const clearRestart = useCallback(() => {
    if (restartRef.current !== null) {
      window.clearTimeout(restartRef.current);
      restartRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    genRef.current += 1;
    const old = recRef.current;
    recRef.current = null;
    runningRef.current = false;
    if (old) {
      try {
        old.close();
      } catch {
        /* already dead */
      }
    }
  }, []);

  const scheduleRestart = useCallback(
    (delay: number) => {
      clearRestart();
      restartRef.current = window.setTimeout(() => {
        restartRef.current = null;
        startRef.current();
      }, delay);
    },
    [clearRestart],
  );

  const startNow = useCallback(() => {
    if (!active || !session?.enabled || runningRef.current || blockedRef.current || !intentRef.current) return;
    if (typeof document !== "undefined" && document.hidden) return;
    clearRestart();
    teardown();
    const gen = genRef.current;

    void (async () => {
      const sdk = await loadSdk();
      if (!sdk || gen !== genRef.current || !intentRef.current) return;
      try {
        const region = session.region ?? "centralindia";
        const cfg = sdk.SpeechConfig.fromAuthorizationToken(tokenRef.current, region);
        // Interview answers contain thinking pauses; the default ~650ms end-of-utterance cuts
        // into them. 900ms keeps sentences whole without feeling laggy.
        cfg.setProperty(sdk.PropertyId.Speech_SegmentationSilenceTimeoutMs, "900");
        cfg.outputFormat = sdk.OutputFormat.Detailed;
        const locales = session.sttLocales?.length ? session.sttLocales : ["en-IN", "en-US", "en-GB"];
        const auto = sdk.AutoDetectSourceLanguageConfig.fromLanguages(locales);
        const audio = sdk.AudioConfig.fromDefaultMicrophoneInput();
        const rec = sdk.SpeechRecognizer.FromConfig(cfg, auto, audio);

        // USAGE: Azure bills real-time STT on audio SENT. The microphone is captured from the start
        // call below until close, and what is captured before the connection opens is sent once it
        // does — so this run's billable audio is start → close, counted only if Azure actually
        // opened a session for it. Reported exactly once, from whichever end comes first.
        const runStartedAt = performance.now();
        let connected = false;
        let usageReported = false;
        const reportUsage = (): void => {
          if (usageReported || !connected) return;
          usageReported = true;
          try {
            usageRef.current?.stt(performance.now() - runStartedAt);
          } catch {
            /* accounting never disturbs recognition */
          }
        };
        rec.sessionStarted = () => {
          connected = true;
        };

        rec.recognizing = (_s, e) => {
          if (gen !== genRef.current) return;
          if (!acceptRef.current(Date.now())) {
            setInterim("");
            return;
          }
          setInterim(e.result.text ?? "");
        };
        rec.recognized = (_s, e) => {
          if (gen !== genRef.current) return;
          if (e.result.reason !== sdk.ResultReason.RecognizedSpeech) return;
          if (!acceptRef.current(Date.now())) {
            setInterim("");
            return;
          }
          const text = e.result.text?.trim();
          setInterim("");
          if (!text) return;
          failuresRef.current = 0; // the mic demonstrably works
          setFinalText((prev) => `${prev} ${text}`.trim());
          // Real measured confidence when the detailed payload carries one; never fabricated.
          try {
            const raw = e.result.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult);
            const parsed = JSON.parse(raw) as { NBest?: { Confidence?: number }[] };
            const c = parsed.NBest?.[0]?.Confidence;
            if (typeof c === "number" && c > 0) {
              confSumRef.current += c;
              confCountRef.current += 1;
              setConfidence(confSumRef.current / confCountRef.current);
            }
          } catch {
            /* detailed payload absent — confidence stays honest-null */
          }
        };
        rec.canceled = (_s, e) => {
          if (gen !== genRef.current) return;
          // Permission refusals are fatal; everything else (network blips, token expiry at the
          // boundary, service hiccups) restarts with backoff — the restart re-reads the fresh
          // token from tokenRef, which is how a mid-interview token refresh takes effect.
          // The SDK has no distinct permission error code; the detail string is the only signal.
          const detail = e.errorDetails ?? "";
          if (/permission|denied|not.?allowed|microphone/i.test(detail)) {
            blockedRef.current = true;
            intentRef.current = false;
            setBlocked(true);
          }
        };
        rec.sessionStopped = () => {
          reportUsage(); // before the stale-generation guard: a superseded run still streamed audio
          if (gen !== genRef.current) return;
          runningRef.current = false;
          setListening(false);
          setInterim("");
          if (!intentRef.current) return;
          failuresRef.current += 1;
          if (failuresRef.current >= MAX_FAILURES) {
            blockedRef.current = true;
            intentRef.current = false;
            setBlocked(true);
          } else {
            scheduleRestart(Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** (failuresRef.current - 1)));
          }
        };

        recRef.current = {
          close: () => {
            reportUsage();
            rec.close();
          },
        };
        rec.startContinuousRecognitionAsync(
          () => {
            if (gen !== genRef.current) return;
            runningRef.current = true;
            setListening(true);
          },
          () => {
            if (gen !== genRef.current) return;
            runningRef.current = false;
            setListening(false);
            failuresRef.current += 1;
            if (failuresRef.current >= MAX_FAILURES) {
              blockedRef.current = true;
              intentRef.current = false;
              setBlocked(true);
            } else if (intentRef.current) {
              scheduleRestart(Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** (failuresRef.current - 1)));
            }
          },
        );
      } catch {
        if (gen !== genRef.current) return;
        failuresRef.current += 1;
        if (failuresRef.current >= MAX_FAILURES) {
          blockedRef.current = true;
          intentRef.current = false;
          setBlocked(true);
        } else if (intentRef.current) {
          scheduleRestart(Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** (failuresRef.current - 1)));
        }
      }
    })();
  }, [active, session, tokenRef, clearRestart, scheduleRestart, teardown]);
  startRef.current = startNow;

  const setIntent = useCallback(
    (want: boolean) => {
      if (blockedRef.current) return;
      if (intentRef.current === want) return;
      intentRef.current = want;
      if (want) {
        failuresRef.current = 0;
        startNow();
      } else {
        clearRestart();
        teardown();
        setListening(false);
        setInterim("");
      }
    },
    [clearRestart, startNow, teardown],
  );

  const reset = useCallback(() => {
    setFinalText("");
    setInterim("");
    setConfidence(null);
    confSumRef.current = 0;
    confCountRef.current = 0;
  }, []);

  useEffect(
    () => () => {
      clearRestart();
      teardown();
    },
    [clearRestart, teardown],
  );

  const transcript = `${finalText} ${interim}`.trim();
  return useMemo(
    () => ({
      supported: Boolean(session?.enabled),
      listening,
      blocked,
      finalText,
      interim,
      transcript,
      confidence,
      setIntent,
      reset,
    }),
    [session, listening, blocked, finalText, interim, transcript, confidence, setIntent, reset],
  );
}

/** Cap any one utterance by its length — a stuck `speaking` flag is a candidate who can never
 *  answer, so the same watchdog philosophy as the Web Speech hook applies here. */
const speakHardCapMs = (text: string): number =>
  Math.min(180_000, 8_000 + text.trim().split(/\s+/).length * 900);

/**
 * Asha's Azure voice — same contract as useAshaSpeech. One synthesizer per utterance, closed on
 * finish/cancel: synthesis is per-question-infrequent, and a fresh SpeakerAudioDestination per
 * utterance is what makes pause/close reliable across browsers.
 *
 * The audio renders through the page (SpeakerAudioDestination → AudioContext), so the mic's echo
 * canceller can see and subtract it — the structural fix for "Asha transcribed into the
 * candidate's answer".
 */
export function useAzureAshaSpeech(
  active: boolean,
  session: SpeechSession | undefined,
  tokenRef: RefObject<string>,
  /** Live usage measurement (speechUsage.ts). Measures only — never changes synthesis. */
  usage: SpeechUsageSink | null = null,
): {
  supported: boolean;
  speaking: boolean;
  speakingRef: RefObject<boolean>;
  speak: (t: string) => void;
  cancel: () => void;
} {
  const [speaking, setSpeaking] = useState(false);
  const speakingRef = useRef(false);
  const genRef = useRef(0);
  const liveRef = useRef<{ close: () => void } | null>(null);
  const watchdogRef = useRef<number | null>(null);
  const usageRef = useRef(usage);
  usageRef.current = usage;

  const setSpeakingBoth = useCallback((v: boolean) => {
    speakingRef.current = v;
    setSpeaking(v);
  }, []);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    genRef.current += 1;
    stopWatchdog();
    const live = liveRef.current;
    liveRef.current = null;
    if (live) {
      try {
        live.close();
      } catch {
        /* already closed */
      }
    }
    setSpeakingBoth(false);
  }, [setSpeakingBoth, stopWatchdog]);

  const speak = useCallback(
    (text: string) => {
      if (!active || !session?.enabled || !text.trim()) return;
      cancel(); // one voice at a time; also bumps gen
      const gen = ++genRef.current;
      // Flag UP first, synchronously — the mic gate reads this ref before the engine makes a sound.
      setSpeakingBoth(true);

      void (async () => {
        const sdk = await loadSdk();
        if (!sdk || gen !== genRef.current) {
          if (gen === genRef.current) setSpeakingBoth(false);
          return;
        }
        try {
          const region = session.region ?? "centralindia";
          const voice = session.ttsVoice ?? "en-IN-NeerjaNeural";
          const cfg = sdk.SpeechConfig.fromAuthorizationToken(tokenRef.current, region);
          cfg.speechSynthesisVoiceName = voice;
          const dest = new sdk.SpeakerAudioDestination();
          const finish = (): void => {
            if (gen !== genRef.current) return;
            stopWatchdog();
            liveRef.current = null;
            try {
              synth.close();
            } catch {
              /* fine */
            }
            setSpeakingBoth(false);
          };
          // The REAL end of Asha's audio, from the playback element itself — not the synthesis
          // result, which completes as soon as the bytes have streamed, well before the sound ends.
          dest.onAudioEnd = finish;
          const audioCfg = sdk.AudioConfig.fromSpeakerOutput(dest);
          const synth = new sdk.SpeechSynthesizer(cfg, audioCfg);
          liveRef.current = {
            close: () => {
              try {
                dest.pause();
              } catch {
                /* fine */
              }
              try {
                synth.close();
              } catch {
                /* fine */
              }
            },
          };
          const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          const ssml =
            `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">` +
            `<voice name="${voice}"><prosody rate="-8%">${esc}</prosody></voice></speak>`;
          // USAGE: Azure bills a synthesis request per character of its SSML body (minus the
          // <speak>/<voice> tags) once it is processed — counted when Azure starts synthesising,
          // so a request that failed outright (bad token, network) is not reported.
          let ttsReported = false;
          synth.synthesisStarted = () => {
            if (ttsReported) return;
            ttsReported = true;
            try {
              usageRef.current?.tts(ttsBillableCharacters(ssml));
            } catch {
              /* accounting never disturbs synthesis */
            }
          };
          synth.speakSsmlAsync(
            ssml,
            (result) => {
              // Synthesis failed outright (bad token, network) → hand the turn back now; on
              // success the destination's onAudioEnd owns the finish.
              if (result.reason !== sdk.ResultReason.SynthesizingAudioCompleted) finish();
            },
            () => finish(),
          );
          stopWatchdog();
          watchdogRef.current = window.setTimeout(finish, speakHardCapMs(text));
        } catch {
          if (gen === genRef.current) setSpeakingBoth(false);
        }
      })();
    },
    [active, session, tokenRef, cancel, setSpeakingBoth, stopWatchdog],
  );

  useEffect(() => cancel, [cancel]);

  return { supported: Boolean(session?.enabled), speaking, speakingRef, speak, cancel };
}
